import type { SiteLang } from "./content/index.js";
import { SELLER } from "./seller.js";

/**
 * Черновики Terms, Refund и Privacy для проверки Paddle и App Review у Meta. ТРЕБУЮТ ПРОВЕРКИ ЮРИСТОМ.
 * Английский текст Privacy продублирован в `docs/meta-app-review/privacy-policy.md`, правьте оба места.
 */
export interface LegalSection {
  id?: string;
  heading: string;
  paragraphs?: string[];
  list?: string[];
}

export interface LegalDoc {
  title: string;
  intro: string;
  sections: LegalSection[];
}

export type LegalKind = "terms" | "refund" | "privacy";

const S = SELLER;

const en: Record<LegalKind, LegalDoc> = {
  terms: {
    title: "Terms of Service",
    intro: `These Terms govern the use of ${S.productName} (the “Service”), provided by ${S.legalName}, registration ${S.registration}, ${S.address} (“we”, “us”). By creating an account or paying for a subscription you agree to these Terms on behalf of your business.`,
    sections: [
      {
        heading: "1. The Service",
        paragraphs: [
          "The Service is booking software with AI replies for small businesses that work by appointment (salons, barbershops and similar). It includes a calendar, a staff app, a public booking page and an AI assistant that answers clients' messages in the business's Instagram account and books appointments into the calendar using only information the business has entered.",
          "The Service is intended for businesses, not for consumers. It does not send bulk or unsolicited messages and does not collect prepayments from the business's clients.",
        ],
      },
      {
        heading: "2. Accounts",
        paragraphs: [
          "The business owner creates the account and may invite staff members. You are responsible for the people you invite and for keeping access to your email secure. Sign-in is by a one-time code sent to your email.",
        ],
      },
      {
        heading: "3. Subscription, trial and payment",
        list: [
          "The subscription costs €25 per month; any applicable tax is calculated by Paddle at checkout. The optional “done for you” setup costs €30 once and is charged together with the subscription.",
          "New subscriptions may start with a free trial of 30 days. A payment method is required to start the trial; the first monthly charge happens when the trial ends unless you cancel before that.",
          "The subscription renews automatically every month until cancelled. You can cancel at any time from the billing page of the app or via the link in Paddle's emails; access continues until the end of the paid period.",
          "Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries and handles returns.",
        ],
      },
      {
        heading: "4. Acceptable use",
        list: [
          "Use the Service only for your own business and in line with applicable law and Instagram/Meta platform rules.",
          "Do not use the Service for marketing campaigns, bulk messaging, messaging people who did not contact you first, or collecting prepayments from clients.",
          "Inform your clients that their messages and bookings are processed by software providers on your behalf (for example via a link to a privacy notice in your profile).",
          "Keep the information in the Service (services, prices, rules) accurate: the AI assistant answers only from it.",
        ],
      },
      {
        heading: "5. AI replies",
        paragraphs: [
          "The AI assistant can make mistakes. It answers only from the business's data and hands the conversation to staff when it does not know the answer, when the client asks for a person, on complaints and on health-related messages. The assistant does not introduce itself as AI, but answers honestly when asked directly; disclosure can be switched on in settings. The business remains responsible for its communication with clients.",
        ],
      },
      {
        heading: "6. Data",
        paragraphs: [
          "For your clients' personal data (messages, names, phone numbers, bookings) the business is the controller and we act as a processor under a data processing agreement. How we handle data is described in our Privacy Policy.",
        ],
      },
      {
        heading: "7. Availability and changes",
        paragraphs: [
          "We aim to keep the Service available but do not guarantee uninterrupted operation. Instagram messaging depends on Meta, which may change or limit its platform. We may change the Service and these Terms; material changes are announced by email at least 14 days in advance.",
        ],
      },
      {
        heading: "8. Liability",
        paragraphs: [
          "To the extent permitted by law, our total liability for any claim is limited to the amounts you paid for the Service in the 3 months before the claim. We are not liable for lost profits, lost bookings or indirect damages.",
        ],
      },
      {
        heading: "9. Termination and your data",
        paragraphs: [
          "You can stop using the Service at any time. On request we provide an export of your clients and bookings (CSV) and delete your data. After a deletion request the data is kept for 30 days in case you change your mind and is then erased permanently. A deletion request cancels the subscription immediately; if you withdraw the request within 30 days, your data is restored, but the Service stays inactive until you start a new subscription (without a free trial).",
        ],
      },
      { heading: "10. Governing law", paragraphs: [`${S.governingLaw}.`] },
      { heading: "11. Contact", paragraphs: [`${S.legalName}, ${S.address}, ${S.email}.`] },
    ],
  },
  refund: {
    title: "Refund Policy",
    intro: `Payments for ${S.productName} are processed by Paddle.com, our Merchant of Record. Refunds are issued by Paddle to the original payment method.`,
    sections: [
      { heading: "Trial", paragraphs: ["If you cancel during the free trial, you are not charged for the subscription."] },
      {
        heading: "Subscription",
        list: [
          "You can cancel at any time; the subscription stays active until the end of the paid month and is not renewed.",
          "If you are not satisfied, you can request a full refund of your first monthly payment within 14 days of that payment.",
          "Later monthly payments are not refunded for partially used periods, except where required by law.",
        ],
      },
      {
        heading: "“Done for you” setup (€30)",
        paragraphs: [
          "Refundable within 14 days of payment if we have not yet started the setup work. Once the setup has been completed, the fee is not refundable.",
        ],
      },
      {
        heading: "How to request a refund",
        paragraphs: [
          `Write to ${S.email} or reply to the Paddle receipt email with your order number. Paddle may also review refund requests under its own buyer terms.`,
        ],
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro: `This policy explains how ${S.legalName} (“we”) processes personal data in ${S.productName} (the “Service”), including data received through the Instagram Platform.`,
    sections: [
      {
        heading: "1. Roles",
        paragraphs: [
          "For the accounts of salon owners and staff we are the controller. For the data of the salon's clients (messages, names, phone numbers, bookings) the salon is the controller and we process the data on its behalf under a data processing agreement.",
        ],
      },
      {
        heading: "2. Data we process",
        list: [
          "Account data: email, name, language, sign-in sessions.",
          "Salon data: services, prices, staff, schedules, rules and answers to frequent questions.",
          "Instagram data (via the Instagram API with Meta Unified Login, after the salon connects its professional account): the account ID and username, an access token (stored encrypted), messages sent to the salon by its clients and replies sent from the Service, the clients' Instagram-scoped IDs and usernames.",
          "Bookings and client cards: name, phone (if given), visit history, notes, no-shows.",
          "Voice messages are transcribed to text and the audio is not stored. Photos and other files are not stored.",
          "Payment data is collected by Paddle; we receive only the subscription status, not card details.",
          "Technical logs and usage records (for example, the number of AI tokens used per salon).",
        ],
      },
      {
        heading: "3. How we use the data",
        list: [
          "To answer the salon's clients and book appointments on the salon's instructions.",
          "To show conversations, bookings and notifications to the salon's staff.",
          "To bill the subscription, keep the Service secure, monitor errors and support the salon.",
          "Messages about health (for example, allergies) are passed to staff and are not sent to the AI model.",
          "We do not sell data, do not use it for advertising and do not use Instagram data for any purpose other than providing the Service to the salon that connected the account.",
        ],
      },
      {
        heading: "4. Service providers",
        paragraphs: [
          "We use: Anthropic (AI model, USA), OpenAI (voice transcription, USA), Railway (hosting, EU West region with processing in the USA under its terms), Resend (email), Paddle (payments, Merchant of Record), Meta (Instagram messaging). Some of them process data outside Serbia and the EU, including in the USA.",
        ],
      },
      {
        heading: "5. Retention",
        list: [
          "Salon and client data is kept while the salon uses the Service.",
          "After a deletion request the salon's data is erased permanently after 30 days.",
          "Technical webhook records are kept for 90 days, AI usage records for 180 days.",
        ],
      },
      {
        id: "data-deletion",
        heading: "6. How to delete your data",
        list: [
          `Salons: write to ${S.email} from the owner's email and ask to delete the account. We confirm within 7 days; the data is erased permanently 30 days after the request. You can also remove our access at any time in Instagram: Settings → Apps and websites.`,
          `Clients of a salon: contact the salon, or write to ${S.email} with your Instagram username and the salon's name; we will forward the request to the salon and delete the data on its instruction.`,
        ],
      },
      {
        heading: "7. Your rights",
        paragraphs: [
          "You can request access to, correction or deletion of your data, restriction of processing, data portability and object to processing. You can also lodge a complaint with the Commissioner for Information of Public Importance and Personal Data Protection of Serbia (poverenik.rs).",
        ],
      },
      { heading: "8. Contact", paragraphs: [`${S.legalName}, ${S.address}, ${S.email}.`] },
    ],
  },
};

const sr: Record<LegalKind, LegalDoc> = {
  terms: {
    title: "Uslovi korišćenja",
    intro: `Ovi Uslovi uređuju korišćenje usluge ${S.productName} („Usluga“) koju pruža ${S.legalName}, registracija ${S.registration}, ${S.address} („mi“). Otvaranjem naloga ili plaćanjem pretplate prihvatate ove Uslove u ime svog preduzeća.`,
    sections: [
      {
        heading: "1. Usluga",
        paragraphs: [
          "Usluga je program za zakazivanje sa AI odgovorima za mala preduzeća koja rade po zakazivanju (saloni, berbernice i slično). Obuhvata kalendar, aplikaciju za majstore, javnu stranicu za zakazivanje i AI asistenta koji odgovara na poruke klijenata na Instagram nalogu preduzeća i upisuje termine u kalendar, isključivo na osnovu podataka koje je preduzeće unelo.",
          "Usluga je namenjena preduzećima, ne potrošačima. Ne šalje masovne ni neželjene poruke i ne naplaćuje avanse klijentima preduzeća.",
        ],
      },
      {
        heading: "2. Nalozi",
        paragraphs: [
          "Vlasnik otvara nalog i može da pozove majstore. Odgovorni ste za osobe koje pozovete i za bezbednost pristupa svom email-u. Prijava je jednokratnim kodom poslatim na email.",
        ],
      },
      {
        heading: "3. Pretplata, probni period i plaćanje",
        list: [
          "Pretplata košta €25 mesečno; porez, ako se obračunava, Paddle računa pri plaćanju. Opcija „povežemo za vas“ košta €30 jednokratno i naplaćuje se zajedno sa pretplatom.",
          "Nova pretplata može početi besplatnim probnim periodom od 30 dana. Za početak je potrebna kartica; prva mesečna naplata je po isteku probnog perioda, osim ako pretplatu ranije otkažete.",
          "Pretplata se obnavlja automatski svakog meseca dok je ne otkažete. Otkazati možete u svakom trenutku na stranici za plaćanje u aplikaciji ili preko linka iz Paddle email-ova; pristup traje do kraja plaćenog perioda.",
          "Porudžbine obrađuje naš onlajn preprodavac Paddle.com. Paddle.com je Merchant of Record za sve naše porudžbine i obrađuje upite kupaca i povraćaje.",
        ],
      },
      {
        heading: "4. Dozvoljeno korišćenje",
        list: [
          "Koristite Uslugu samo za svoje preduzeće, u skladu sa zakonom i pravilima Instagram/Meta platforme.",
          "Ne koristite Uslugu za marketinške kampanje, masovne poruke, pisanje osobama koje vam se nisu prve javile ni za naplatu avansa od klijenata.",
          "Obavestite klijente da njihove poruke i termine u vaše ime obrađuju pružaoci softvera (na primer linkom ka obaveštenju o privatnosti u profilu).",
          "Održavajte tačne podatke (usluge, cene, pravila): AI asistent odgovara samo na osnovu njih.",
        ],
      },
      {
        heading: "5. AI odgovori",
        paragraphs: [
          "AI asistent može da pogreši. Odgovara samo iz podataka preduzeća i predaje razgovor majstoru kada ne zna odgovor, kada klijent traži osobu, kod žalbi i poruka o zdravlju. Asistent se sam ne predstavlja kao AI, ali na direktno pitanje odgovara iskreno; otkrivanje se može uključiti u podešavanjima. Preduzeće ostaje odgovorno za komunikaciju sa klijentima.",
        ],
      },
      {
        heading: "6. Podaci",
        paragraphs: [
          "Za podatke o ličnosti vaših klijenata (poruke, imena, telefoni, termini) preduzeće je rukovalac, a mi obrađivač na osnovu ugovora o obradi. Način obrade opisan je u Politici privatnosti.",
        ],
      },
      {
        heading: "7. Dostupnost i izmene",
        paragraphs: [
          "Trudimo se da Usluga bude dostupna, ali ne garantujemo neprekidan rad. Poruke na Instagramu zavise od kompanije Meta, koja može da menja ili ograniči svoju platformu. Uslugu i ove Uslove možemo menjati; o bitnim izmenama obaveštavamo email-om najmanje 14 dana unapred.",
        ],
      },
      {
        heading: "8. Odgovornost",
        paragraphs: [
          "U meri u kojoj zakon dozvoljava, naša ukupna odgovornost ograničena je na iznose koje ste platili za Uslugu u 3 meseca pre zahteva. Ne odgovaramo za izgubljenu dobit, izgubljene termine ni posrednu štetu.",
        ],
      },
      {
        heading: "9. Prestanak i vaši podaci",
        paragraphs: [
          "Možete prestati da koristite Uslugu u svakom trenutku. Na zahtev dostavljamo izvoz klijenata i termina (CSV) i brišemo vaše podatke. Posle zahteva za brisanje podaci se čuvaju 30 dana za slučaj da se predomislite, a zatim se trajno brišu. Zahtev za brisanje odmah otkazuje pretplatu; ako ga povučete u roku od 30 dana, podaci se vraćaju, ali Usluga ostaje neaktivna dok ne pokrenete novu pretplatu (bez probnog perioda).",
        ],
      },
      { heading: "10. Merodavno pravo", paragraphs: [`${S.governingLaw}.`] },
      { heading: "11. Kontakt", paragraphs: [`${S.legalName}, ${S.address}, ${S.email}.`] },
    ],
  },
  refund: {
    title: "Politika povraćaja",
    intro: `Plaćanja za ${S.productName} obrađuje Paddle.com, naš Merchant of Record. Povraćaj vrši Paddle na isti način plaćanja.`,
    sections: [
      { heading: "Probni period", paragraphs: ["Ako otkažete tokom besplatnog probnog perioda, pretplata se ne naplaćuje."] },
      {
        heading: "Pretplata",
        list: [
          "Otkazati možete u svakom trenutku; pretplata važi do kraja plaćenog meseca i ne obnavlja se.",
          "Ako niste zadovoljni, možete tražiti pun povraćaj prve mesečne uplate u roku od 14 dana od te uplate.",
          "Kasnije mesečne uplate se ne vraćaju za delimično iskorišćen period, osim kada to zakon zahteva.",
        ],
      },
      {
        heading: "„Povežemo za vas“ (€30)",
        paragraphs: ["Vraća se u roku od 14 dana od uplate ako još nismo počeli podešavanje. Posle završenog podešavanja iznos se ne vraća."],
      },
      {
        heading: "Kako tražiti povraćaj",
        paragraphs: [
          `Pišite na ${S.email} ili odgovorite na Paddle email sa računom i navedite broj porudžbine. Paddle može razmotriti zahtev i prema svojim uslovima za kupce.`,
        ],
      },
    ],
  },
  privacy: {
    title: "Politika privatnosti",
    intro: `Ova politika objašnjava kako ${S.legalName} („mi“) obrađuje podatke o ličnosti u usluzi ${S.productName} („Usluga“), uključujući podatke dobijene preko Instagram platforme.`,
    sections: [
      {
        heading: "1. Uloge",
        paragraphs: [
          "Za naloge vlasnika i majstora salona mi smo rukovalac. Za podatke klijenata salona (poruke, imena, telefoni, termini) rukovalac je salon, a mi ih obrađujemo u njegovo ime na osnovu ugovora o obradi.",
        ],
      },
      {
        heading: "2. Koje podatke obrađujemo",
        list: [
          "Podaci naloga: email, ime, jezik, sesije prijave.",
          "Podaci salona: usluge, cene, majstori, rasporedi, pravila i odgovori na česta pitanja.",
          "Podaci sa Instagrama (preko Instagram API sa Meta Unified Login prijavom, kada salon poveže profesionalni nalog): ID i korisničko ime naloga, pristupni token (čuva se šifrovan), poruke klijenata salonu i odgovori poslati iz Usluge, Instagram ID-jevi i korisnička imena klijenata.",
          "Termini i kartice klijenata: ime, telefon (ako je dat), istorija poseta, beleške, nedolasci.",
          "Glasovne poruke se pretvaraju u tekst, a zvuk se ne čuva. Fotografije i drugi fajlovi se ne čuvaju.",
          "Podatke o plaćanju prikuplja Paddle; mi dobijamo samo status pretplate, ne podatke kartice.",
          "Tehnički zapisi i evidencija korišćenja (na primer broj AI tokena po salonu).",
        ],
      },
      {
        heading: "3. Kako koristimo podatke",
        list: [
          "Da odgovorimo klijentima salona i zakažemo termine po uputstvima salona.",
          "Da prikažemo razgovore, termine i obaveštenja majstorima salona.",
          "Za naplatu pretplate, bezbednost Usluge, praćenje grešaka i podršku salonu.",
          "Poruke o zdravlju (na primer alergije) prosleđuju se majstoru i ne šalju se AI modelu.",
          "Podatke ne prodajemo, ne koristimo za reklame i podatke sa Instagrama koristimo samo za pružanje Usluge salonu koji je povezao nalog.",
        ],
      },
      {
        heading: "4. Pružaoci usluga",
        paragraphs: [
          "Koristimo: Anthropic (AI model, SAD), OpenAI (prepoznavanje govora, SAD), Railway (hosting, region EU West uz obradu u SAD prema njihovim uslovima), Resend (email), Paddle (plaćanja, Merchant of Record), Meta (Instagram poruke). Neki od njih obrađuju podatke van Srbije i EU, uključujući SAD.",
        ],
      },
      {
        heading: "5. Čuvanje",
        list: [
          "Podaci salona i klijenata čuvaju se dok salon koristi Uslugu.",
          "Posle zahteva za brisanje podaci salona se trajno brišu nakon 30 dana.",
          "Tehnički zapisi webhook-ova čuvaju se 90 dana, evidencija korišćenja AI 180 dana.",
        ],
      },
      {
        id: "data-deletion",
        heading: "6. Kako obrisati podatke",
        list: [
          `Saloni: pišite na ${S.email} sa email-a vlasnika i zatražite brisanje naloga. Potvrđujemo u roku od 7 dana; podaci se trajno brišu 30 dana posle zahteva. Pristup možete ukinuti i sami u Instagramu: Podešavanja → Aplikacije i veb-sajtovi.`,
          `Klijenti salona: obratite se salonu ili pišite na ${S.email} sa svojim Instagram korisničkim imenom i nazivom salona; zahtev prosleđujemo salonu i brišemo podatke po njegovom uputstvu.`,
        ],
      },
      {
        heading: "7. Vaša prava",
        paragraphs: [
          "Možete tražiti pristup, ispravku ili brisanje podataka, ograničenje obrade, prenosivost i uložiti prigovor. Pritužbu možete podneti Povereniku za informacije od javnog značaja i zaštitu podataka o ličnosti (poverenik.rs).",
        ],
      },
      { heading: "8. Kontakt", paragraphs: [`${S.legalName}, ${S.address}, ${S.email}.`] },
    ],
  },
};

export const legalDocs: Record<SiteLang, Record<LegalKind, LegalDoc>> = { sr, en };
